import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  updateProviderItem,
  UpdateProviderItemReq,
} from '@/app/apis/Providers/requests'
import { useToast } from '@/shad/ui/toast/use-toast'
import { parseRequestError } from '@/common/utilities'
import { ProviderQueryKeys } from '@/queries/providers/queries'
import {
  ProviderItemCategoryList,
  ProviderItem,
} from '@/queries/providers/schema'
import { TransformedProduct } from '../../ProductForm/add-product.schema'

interface UseUpdateProductOptions {
  providerId: number
  programId: number
  onSuccess?: () => void
  onError?: (error: Error) => void
}

export const useUpdateProduct = ({
  providerId,
  programId,
  onSuccess,
  onError,
}: UseUpdateProductOptions) => {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: async ({
      item_id,
      payload,
    }: {
      item_id: number
      payload: TransformedProduct
    }) => {
      const req: UpdateProviderItemReq = {
        provider_id: providerId,
        item_id,
        payload,
      }
      return await updateProviderItem(req)
    },
    onSuccess: (updatedItem: ProviderItem) => {
      // Update the cached provider item categories
      queryClient.setQueryData(
        ProviderQueryKeys.providerItemCategories(providerId, programId),
        (old: ProviderItemCategoryList | undefined) => {
          if (!old) return old
          return old.map((category) => ({
            ...category,
            provider_items: category.provider_items.map((item) =>
              item.id === updatedItem.id ? { ...item, ...updatedItem } : item
            ),
          }))
        }
      )
      toast({
        title: 'Product Updated',
        description: 'Product information has been updated successfully.',
        variant: 'default',
      })
      onSuccess?.()
    },
    onError: (error: Error) => {
      const errorMessage = parseRequestError(error).message
      toast({
        title: 'Update Failed',
        description: errorMessage,
        variant: 'destructive',
      })
      onError?.(error)
    },
  })
}
